import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../App'
import { api, User } from '../services/api'

export default function ProfilePage() {
  const { userId } = useParams<{ userId?: string }>()
  const { user: currentUser, logout } = useAuth()
  const navigate = useNavigate()

  const [profileUser, setProfileUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [publicKeyFingerprint, setPublicKeyFingerprint] = useState<string>('')
  const [isEditingName, setIsEditingName] = useState(false)
  const [editName, setEditName] = useState('')
  const [isEditingStatus, setIsEditingStatus] = useState(false)
  const [editStatus, setEditStatus] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isOwnProfile = !userId || userId === currentUser?.username

  useEffect(() => {
    if (isOwnProfile) {
      if (currentUser) {
        setProfileUser({ id: currentUser.id, username: currentUser.username })
        const identityKey = localStorage.getItem('lynkstar_identity_key')
        if (identityKey) {
          try { setPublicKeyFingerprint(JSON.parse(identityKey).publicKey.substring(0, 16)) }
          catch { setPublicKeyFingerprint('Not available') }
        }
        // Load full profile from API
        api.getCurrentUser().then(u => {
          setProfileUser(u)
          setEditName(u.display_name || u.username)
          setEditStatus(u.status_text || '')
        }).catch(() => {})
      }
    } else {
      loadUserProfile(userId!)
    }
  }, [userId, currentUser, isOwnProfile])

  const loadUserProfile = async (username: string) => {
    setIsLoading(true)
    try {
      const user = await api.getUserByUsername(username)
      setProfileUser(user)
      if (user.publicKey) setPublicKeyFingerprint(user.publicKey.substring(0, 16))
    } catch {} finally { setIsLoading(false) }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { alert('頭像大小不可超過 5MB'); return }
    setIsUploading(true)
    try {
      const result = await api.uploadAvatar(file)
      await api.updateProfile({ avatar_url: result.url })
      setProfileUser(prev => prev ? { ...prev, avatar: result.url, avatar_url: result.url } : prev)
    } catch { alert('上傳失敗') }
    finally { setIsUploading(false) }
  }

  const handleSaveName = async () => {
    if (!editName.trim()) return
    try {
      await api.updateProfile({ display_name: editName.trim() })
      setProfileUser(prev => prev ? { ...prev, display_name: editName.trim() } : prev)
      setIsEditingName(false)
    } catch { alert('更新失敗') }
  }

  const handleSaveStatus = async () => {
    try {
      await api.updateProfile({ status_text: editStatus.trim() })
      setProfileUser(prev => prev ? { ...prev, status_text: editStatus.trim() } : prev)
      setIsEditingStatus(false)
    } catch { alert('更新失敗') }
  }

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-bg"><div className="spinner border-primary border-t-primary-light" /></div>

  const avatarUrl = profileUser?.avatar || profileUser?.avatar_url

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-dark-bg">
      <header className="app-bar">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg></button>
        <h1 className="text-lg font-semibold flex-1">{isOwnProfile ? '個人檔案' : '用戶資料'}</h1>
        {isOwnProfile && <button onClick={() => navigate('/settings')} className="p-2 hover:bg-white/10 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg></button>}
      </header>

      <main className="flex-1 overflow-y-auto">
        {/* Avatar Section */}
        <div className="flex flex-col items-center py-8 px-4">
          <div className="relative">
            <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center text-white text-3xl font-semibold shadow-lg overflow-hidden">
              {isUploading ? <div className="spinner border-white border-t-white/30" /> : avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : profileUser?.username?.charAt(0).toUpperCase()}
            </div>
            {isOwnProfile && (
              <button onClick={() => fileInputRef.current?.click()} className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center border-2 border-white dark:border-dark-bg shadow">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
          </div>

          {/* Display name */}
          {isOwnProfile && isEditingName ? (
            <div className="mt-4 flex items-center gap-2">
              <input value={editName} onChange={e => setEditName(e.target.value)} className="text-xl font-bold text-center bg-gray-100 dark:bg-gray-800 rounded-xl px-3 py-1 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary" autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveName()} />
              <button onClick={handleSaveName} className="p-1 text-primary"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></button>
            </div>
          ) : (
            <button onClick={() => isOwnProfile && setIsEditingName(true)} className="mt-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{profileUser?.display_name || profileUser?.username}</h2>
              {profileUser?.display_name && <p className="text-sm text-gray-500">@{profileUser?.username}</p>}
            </button>
          )}

          {/* Status */}
          {isOwnProfile && isEditingStatus ? (
            <div className="mt-2 flex items-center gap-2">
              <input value={editStatus} onChange={e => setEditStatus(e.target.value)} placeholder="設定狀態..." className="text-sm text-center bg-gray-100 dark:bg-gray-800 rounded-xl px-3 py-1 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary" autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveStatus()} />
              <button onClick={handleSaveStatus} className="p-1 text-primary"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></button>
            </div>
          ) : (
            <button onClick={() => isOwnProfile && setIsEditingStatus(true)} className="mt-1">
              <p className="text-sm text-gray-500 dark:text-gray-400">{profileUser?.status_text || (isOwnProfile ? '點擊設定狀態' : '')}</p>
            </button>
          )}
        </div>

        {/* Security Section */}
        <div className="px-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              <span className="font-semibold text-blue-700 dark:text-blue-300">安全金鑰指紋</span>
            </div>
            <p className="text-sm text-blue-600 dark:text-blue-400 font-mono">{publicKeyFingerprint || 'Loading...'}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-4 space-y-3">
          {!isOwnProfile && (
            <>
              <button onClick={() => profileUser && navigate(`/chat/${profileUser.username}`)} className="btn-primary w-full flex items-center justify-center gap-2">發送訊息</button>
              <button onClick={() => navigate(`/call/${profileUser?.username}?type=audio`)} className="w-full px-4 py-3 bg-gray-100 dark:bg-dark-surface hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl flex items-center justify-center gap-2 text-gray-700 dark:text-white">語音通話</button>
              <button onClick={() => navigate(`/call/${profileUser?.username}?type=video`)} className="w-full px-4 py-3 bg-gray-100 dark:bg-dark-surface hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl flex items-center justify-center gap-2 text-gray-700 dark:text-white">視訊通話</button>
            </>
          )}
          {isOwnProfile ? (
            <button onClick={() => setShowLogoutConfirm(true)} className="w-full px-4 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 rounded-xl flex items-center justify-center gap-2 text-red-600">登出</button>
          ) : (
            <button onClick={async () => { if (profileUser) { try { await api.blockUser(profileUser.id); navigate(-1) } catch {} } }} className="w-full px-4 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 rounded-xl flex items-center justify-center gap-2 text-red-600">封鎖用戶</button>
          )}
        </div>

        <div className="px-4 mt-8 pb-8 text-center text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center justify-center gap-1 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-secure-green" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            <span>Signal Protocol 端對端加密</span>
          </div>
        </div>
      </main>

      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowLogoutConfirm(false)}>
          <div className="bg-white dark:bg-dark-surface w-full max-w-sm rounded-2xl p-6 animate-fade-in" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">確定登出？</h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">加密金鑰儲存在本地，登出前請確保已備份。</p>
            <div className="flex gap-3">
              <button onClick={() => setShowLogoutConfirm(false)} className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 rounded-xl text-gray-700 dark:text-white">取消</button>
              <button onClick={() => { logout(); navigate('/login') }} className="flex-1 px-4 py-3 bg-red-500 rounded-xl text-white">登出</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
